/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Comprobantes de evidencia de la venta (foto del cheque, comprobante de
 * transferencia). Mirror de la sección del detalle de movimiento de cliente:
 * listar / ver (signed URL) / adjuntar / borrar — todo best-effort, la venta
 * ya está registrada.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  type StyleProp,
  type TextStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { AttachmentPreviewModal } from '../../shared/attachments/components/AttachmentPreviewModal';
import { pickAttachmentInteractive } from '../../shared/attachments/infra/pickAttachmentFile';
import { isUnexpectedFailureHandled } from '../../shared/sentry/presentUnexpectedFailure';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import {
  createSaleAttachmentUrl,
  deleteSaleAttachment,
  listSaleAttachments,
  saveSaleAttachment,
  type SaleAttachmentRow,
} from '../infra/saleAttachmentsRepo';

type Props = {
  saleId: string;
  /** Venta anulada: solo lectura (no adjuntar ni borrar). */
  saleVoided?: boolean;
  /** Estilo del título de sección — desktop usa formSectionTitle, mobile mobileSectionTitle. */
  titleStyle?: StyleProp<TextStyle>;
};

export function SaleAttachmentsSection({ saleId, saleVoided = false, titleStyle }: Props) {
  const user = useAuthStore((s) => s.user);
  const [attachments, setAttachments] = useState<SaleAttachmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewMime, setPreviewMime] = useState('application/octet-stream');

  const canManage = !saleVoided;

  const reload = useCallback(async () => {
    if (!saleId) return;
    setLoading(true);
    try {
      const rows = await listSaleAttachments(saleId);
      setAttachments(rows);
    } finally {
      setLoading(false);
    }
  }, [saleId]);

  useEffect(() => {
    setAttachments([]);
    setError(null);
    void reload();
  }, [reload]);

  const handleAttach = useCallback(async () => {
    if (busy || !canManage) return;
    setError(null);
    setBusy(true);
    try {
      const picked = await pickAttachmentInteractive('sales');
      if (!picked) return;
      const orgId = await getSessionOrganizationId();
      if (!orgId) {
        setError('No pude identificar la organización para adjuntar.');
        return;
      }
      await saveSaleAttachment({
        attachmentId: crypto.randomUUID(),
        saleId,
        orgId,
        fileName: picked.fileName,
        mimeType: picked.mimeType,
        fileSize: picked.fileSize,
        fileBase64: picked.base64,
        createdBy: user?.id ?? null,
      });
      await reload();
    } catch (e) {
      if (isUnexpectedFailureHandled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [busy, canManage, saleId, reload, user?.id]);

  const confirmDelete = useCallback(async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!id) return;
    setError(null);
    setBusy(true);
    try {
      await deleteSaleAttachment(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [pendingDeleteId, reload]);

  const openAttachment = useCallback(async (attachment: SaleAttachmentRow) => {
    setError(null);
    if (!attachment.fileUrl) {
      setError('El comprobante todavía no está disponible.');
      return;
    }
    const url = await createSaleAttachmentUrl(attachment.fileUrl);
    if (!url) {
      setError('No pude generar el enlace del comprobante.');
      return;
    }
    setPreviewFileName(attachment.fileName);
    setPreviewMime(attachment.mimeType);
    setPreviewUri(url);
  }, []);

  return (
    <View testID="sales.detail.attachments">
      <Text style={[modalExperience.formSectionTitle, titleStyle]}>Comprobantes</Text>
      {loading ? (
        <View style={modalExperience.stateBox}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={modalExperience.stateText}>Cargando…</Text>
        </View>
      ) : attachments.length > 0 ? (
        <View style={styles.attachmentList}>
          {attachments.map((a) => (
            <View key={a.id} style={styles.attachmentRow}>
              <TouchableOpacity
                onPress={() => void openAttachment(a)}
                accessibilityRole="button"
                accessibilityLabel={`Ver ${a.fileName}`}
                style={styles.attachmentInfo}
                testID={`sales.detail.attachment.${a.id}`}
              >
                <Ionicons
                  name={a.mimeType.startsWith('image/') ? 'image-outline' : 'document-text-outline'}
                  size={20}
                  color={ticketColors.ink2}
                />
                <View style={styles.attachmentTextCol}>
                  <Text style={styles.attachmentFileName} numberOfLines={1}>
                    {a.fileName}
                  </Text>
                  <Text style={styles.attachmentMeta}>Ver comprobante</Text>
                </View>
              </TouchableOpacity>
              {canManage ? (
                <TouchableOpacity
                  onPress={() => setPendingDeleteId(a.id)}
                  disabled={busy}
                  accessibilityRole="button"
                  accessibilityLabel={`Borrar ${a.fileName}`}
                  style={styles.deleteBtn}
                  testID={`sales.detail.attachment.delete.${a.id}`}
                >
                  <Ionicons name="trash-outline" size={16} color={ticketColors.bad} />
                </TouchableOpacity>
              ) : null}
            </View>
          ))}
        </View>
      ) : (
        <ModalStatePanel
          empty
          emptyTitle="Sin comprobantes"
          emptyMessage="Esta venta no tiene archivos adjuntos."
        />
      )}
      {error ? <Text style={styles.attachError}>{error}</Text> : null}
      {canManage ? (
        <TouchableOpacity
          onPress={() => void handleAttach()}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel="Adjuntar comprobante"
          style={styles.attachCta}
          testID="sales.detail.attach-cta"
        >
          <Ionicons name="attach-outline" size={18} color={ticketColors.gold} />
          <Text style={styles.attachCtaText}>
            {busy
              ? 'Subiendo…'
              : attachments.length > 0
                ? 'Adjuntar otro comprobante'
                : 'Adjuntar comprobante'}
          </Text>
        </TouchableOpacity>
      ) : null}

      <AttachmentPreviewModal
        visible={previewUri != null}
        uri={previewUri}
        fileName={previewFileName}
        mimeType={previewMime}
        onClose={() => setPreviewUri(null)}
      />
      <OperationalConfirmModal
        visible={pendingDeleteId != null}
        presentation="overlay"
        title="Borrar comprobante"
        message="Se borra el archivo adjunto. La venta no cambia."
        confirmLabel="Borrar"
        destructive
        loading={busy}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => void confirmDelete()}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  attachmentList: {
    gap: spacing.xs,
  },
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  attachmentInfo: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  attachmentTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  attachmentFileName: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  attachmentMeta: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  deleteBtn: {
    padding: 4,
  },
  attachError: {
    marginTop: spacing.xs,
    fontSize: 12,
    color: ticketColors.bad,
    fontFamily: ticketFonts.sans,
  },
  attachCta: {
    marginTop: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: 'transparent',
  },
  attachCtaText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
